// The deposit QR: what it says, and which modules are drawn.
//
// MONEY PATH. A person scans this and sends to what it says, so it says one
// thing only: the documented TON transfer link of the address the dialog
// shows and copies — the same string, not a form derived again.
//
// WHAT IT SAYS (ADR-012: the official source, not a remembered constant).
// docs.ton.org → "Deep links" → "GRAM transfer to address", read 2026-10-13:
//
//     ton://transfer/<ADDRESS>
//
// "Opens the pre-filled Send screen and prompts the user to enter the missing
// data"; `<ADDRESS>` is "the recipient's TON address", and the page's example
// carries a UQ… (user-friendly, non-bounceable) one — the form the dialog
// shows. `amount` and `text` are optional there and are NOT put in: a deposit
// is of any amount and carries no comment. The address is not re-encoded: the
// user-friendly form is base64url, which a URL path takes as it is.
//
// HOW IT IS DRAWN. Level Q (a quarter of the codewords can be lost) and no
// border: the white plate around the symbol is its quiet zone. A square in the
// middle is left clear for the TON mark. At level Q a 48-character address is
// version 6 — 41 modules, whose only alignment pattern stands near the bottom
// right corner, so the 9-module hole (under 5% of the symbol) lies on data
// alone; at level H the same text would be version 7, which has an alignment
// pattern exactly where the mark goes, and smaller modules for the same plate.
// depositQrModules.test.ts reads every drawing back with a decoder that is not this
// encoder, with the hole empty, with the mark, and with the hole solid ink.

import { encode } from 'uqr'

const TON_TRANSFER = 'ton://transfer/'

/** The transfer link of a deposit address: the address as it is shown, after
 *  the scheme, and nothing else. */
export function depositLink(address: string): string {
  return `${TON_TRANSFER}${address}`
}

export interface DepositQrModules {
  /** modules a side */
  size: number
  /** dark[y][x]: the modules that are DRAWN — the symbol's own, less the hole */
  dark: boolean[][]
  /** the square left clear for the mark: modules [from, to) on both axes */
  hole: { from: number; to: number }
  /** the top-left corners of the three 7×7 finder patterns, as [x, y] */
  finders: readonly (readonly [number, number])[]
}

/** The QR of `text`, with the hole for the mark already taken out. */
export function depositQr(text: string): DepositQrModules {
  const { size, data } = encode(text, { ecc: 'Q', border: 0 })
  // an odd number of modules, a little over a fifth of the side, in the middle
  // (a symbol's side is odd, so the hole is centred to the module)
  const side = 2 * Math.floor(size * 0.11) + 1
  const from = (size - side) / 2
  const to = from + side
  const dark = data.map((row, y) =>
    row.map((on, x) => on && !(x >= from && x < to && y >= from && y < to))
  )
  return {
    size,
    dark,
    hole: { from, to },
    finders: [
      [0, 0],
      [size - 7, 0],
      [0, size - 7],
    ],
  }
}

// one run of dark modules of a row, x0 up to x1 (not included), as a closed
// subpath in absolute commands; a corner is rounded when the module above (or
// below) it is light — its other neighbour, outside the run, always is
const R = 0.38
function run(dark: boolean[][], y: number, x0: number, x1: number): string {
  const lit = (x: number, yy: number) => dark[yy]?.[x] === true
  const tl = lit(x0, y - 1) ? 0 : R
  const tr = lit(x1 - 1, y - 1) ? 0 : R
  const br = lit(x1 - 1, y + 1) ? 0 : R
  const bl = lit(x0, y + 1) ? 0 : R
  const arc = (r: number, x: number, yy: number) => (r ? `A${r} ${r} 0 0 1 ${x} ${yy}` : '')
  return [
    `M${x0 + tl} ${y}`,
    `H${x1 - tr}`,
    arc(tr, x1, y + tr),
    `V${y + 1 - br}`,
    arc(br, x1 - br, y + 1),
    `H${x0 + bl}`,
    arc(bl, x0, y + 1 - bl),
    `V${y + tl}`,
    arc(tl, x0 + tl, y),
    'Z',
  ].join('')
}

/** The drawn modules as one svg path in module units: runs along each row,
 *  their free corners rounded. The finder patterns are left out — they are
 *  drawn as three rounded squares each (DepositQr.tsx). */
export function depositQrPath({ size, dark, finders }: DepositQrModules): string {
  const inFinder = (x: number, y: number) =>
    finders.some(([fx, fy]) => x >= fx && x < fx + 7 && y >= fy && y < fy + 7)
  const drawn = dark.map((row, y) => row.map((on, x) => on && !inFinder(x, y)))
  const parts: string[] = []
  for (let y = 0; y < size; y++) {
    let x = 0
    while (x < size) {
      if (!drawn[y]?.[x]) {
        x++
        continue
      }
      const x0 = x
      while (x < size && drawn[y]?.[x]) x++
      parts.push(run(drawn, y, x0, x))
    }
  }
  return parts.join('')
}
